<template>
  <div class="login login--contrast">
    <div class="container">
      <div class="row row-lg-right row-xl-right">
        <div class="col col-xs-12 col-md-8 col-lg-6">
          <div class="login__text">{{ glueUpPrepositions(mainPageInfo) }}</div>
        </div>
        <div class="col col-xs-12 col-md-6 col-lg-4 col-lg-6">
          <div class="login__card">
            <el-card class="box-card" :body-style="{ padding: '40px 36px' }">
              <h1 class="login__title">
                Sign in to AllTeams
              </h1>
              <div class="login__no-account">
                No account?
                <router-link :to="{ name: registrationRoute }">
                  <el-link>Create one</el-link>
                </router-link>
              </div>

              <external-login />

              <el-divider content-position="center">or</el-divider>

              <div class="login__form">
                <el-form @submit.prevent="validateForm" label-width="0">
                  <el-form-item :error="errors.email">
                    <el-input v-model="loginForm.email" placeholder="Email" />
                  </el-form-item>

                  <el-form-item :error="errors.password">
                    <el-input
                      placeholder="Password"
                      v-model="loginForm.password"
                      show-password
                    ></el-input>
                  </el-form-item>

                  <div class="login__forgot-password">
                    <router-link :to="{ name: 'PasswordReset' }">
                      <el-link target="_blank">Forgotten password?</el-link>
                    </router-link>
                  </div>

                  <el-button
                    type="primary"
                    native-type="submit"
                    class="login__submit-button"
                    :disabled="isButtonDisabled('login')"
                  >
                    Sign in
                  </el-button>
                </el-form>
              </div>
            </el-card>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Icon from '@/components/common/Icon.vue';
import ExternalLogin from '@/components/unregisteredUsersPages/ExternalLogin.vue';
import { AUTH_REQUEST, GET_USER_CONTACT_INFO } from '@/store/mutationTypes.js';
import { confirmEmail } from '@/api/user.js';

import { disableButton } from '@/mixins/disableButton.js';
import { showMessage } from '@/utils/messages.js';
import { parseErrorsByFields, glueUpPrepositions } from '@/utils/index.js';
import { REGISTRATION_ONLY_BY_INVITE } from '@/settings.js';

export default {
  name: 'Login',
  components: {
    Icon,
    ExternalLogin,
  },
  mixins: [disableButton],
  data() {
    return {
      loginForm: {
        email: '',
        password: '',
      },
      errors: {
        email: '',
        password: '',
      },
      mainPageInfo: `Welcome to AllTeams! \n The place where captains can find heroes and heroes can find adventures. \n Please login or register to start your journey. \n All right reserved to Magic Tavern Ltd`,
    };
  },
  computed: {
    isFormFilled() {
      const { loginForm } = this;
      return Object.values(loginForm).every((value) => !!value);
    },
    confirmationCode() {
      return this.$route.query.code || null;
    },
    registrationRoute() {
      return REGISTRATION_ONLY_BY_INVITE ? 'WantToRegister' : 'Registration';
    },
  },
  methods: {
    showMessage,
    parseErrorsByFields,
    glueUpPrepositions,
    async authorize() {
      this.disableButton('login');
      const { email, password } = this.loginForm;
      await this.$store.dispatch(AUTH_REQUEST, { email, password });
      this.enableButton('login');

      const errors = this.$store.state.user.authErrors;
      if (errors) {
        this.errors = { ...this.errors, ...this.parseErrorsByFields(errors) };
      } else {
        this.clearErrors();
        this.$store.dispatch(GET_USER_CONTACT_INFO);
      }
    },
    async requestConfirmEmail() {
      this.disableButton('login');
      const { confirmationCode, loginForm } = this;
      const { success, errors } = await confirmEmail({
        code: confirmationCode,
        email: loginForm.email,
      });
      this.enableButton('login');

      if (success) {
        this.clearErrors();
        this.authorize();
      } else if (errors) {
        this.errors = { ...this.errors, ...this.parseErrorsByFields(errors) };
      }
    },
    validateForm() {
      const { confirmationCode, loginForm, isFormFilled } = this;

      if (confirmationCode && loginForm.email && loginForm.email.trim()) {
        this.requestConfirmEmail();
      } else if (isFormFilled) {
        this.authorize();
      } else {
        this.showMessage({
          type: 'error',
          message: 'Please enter your login and password',
        });
      }
    },
    clearErrors() {
      this.errors = {
        email: '',
        password: '',
      };
    },
  },
};
</script>

<style lang="scss">
@import '~@/styles/shared/_globals.scss';

.login {
  min-height: 100vh;
  width: 100%;
  min-width: 100%;
  min-height: 100vh;

  padding-top: var(--global-dimension-size-250);
  padding-bottom: var(--global-dimension-size-250);
  box-sizing: border-box;

  background: url('~@/assets/background.jpg');
  background-size: cover;
  background-repeat: no-repeat;
  background-position: left;

  @include from('xl') {
    padding-top: 15vh;
  }

  &--contrast {
    padding-top: var(--global-dimension-size-250);
    padding-bottom: var(--global-dimension-size-250);
    position: relative;
    z-index: 1;

    @include from('xl') {
      padding-top: 15vh;
    }

    &:before {
      position: absolute;
      content: '';
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      background-color: var(--color-gray-900);
      opacity: 0.5;
      z-index: -1;
    }
  }

  &__card {
    @include from('xl') {
      max-width: 426px;
      margin: auto;
    }
  }

  &__title {
    margin: 0;
    margin-bottom: 10px;

    font-size: 24px;
    font-weight: normal;
  }

  &__no-account {
    font-size: 16px;

    &--reset-password {
      margin-bottom: 24px;
    }

    &--padding-bottom {
      margin-bottom: 24px;
    }
  }

  &__sign-in-links {
    text-align: center;
    margin: 30px 0 40px;
  }

  &__sign-in-link {
    margin: 0 auto;

    & + & {
      margin-top: 20px;
    }

    button {
      width: 270px;
    }
  }

  &__sign-in-icon {
    margin-right: 10px;
    vertical-align: bottom;
  }

  &__forgot-password {
    margin-top: -10px;
    margin-bottom: var(--global-dimension-size-200);
  }

  &__submit-button {
    display: block;
    margin: 0 auto;
  }

  &__text {
    margin-bottom: var(--global-dimension-size-550);
    font-weight: var(--global-font-weight-medium);
    font-size: var(--font-size-400);
    color: var(--color-gray-50);
    white-space: pre-line;

    @include from('lg') {
      font-size: var(--font-size-800);
      margin: 0 var(--global-dimension-size-550)
        var(--global-dimension-size-1200);
    }

    @include from('xxl') {
      margin-left: var(--global-dimension-size-1700);
    }
  }

  &__privacy-confirm {
    margin-top: var(--global-dimension-size-300);
    font-size: var(--font-size-200);
  }
}
</style>
